import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Building2, Calendar, Briefcase, GraduationCap, Users, Award, CheckCircle } from 'lucide-react';
import { experience } from '../data/mock';
import { useInView } from '../hooks/useInView';

const ExperiencePage = () => {
  const [headerRef, headerInView] = useInView({ threshold: 0.1 });
  const [timelineRef, timelineInView] = useInView({ threshold: 0.1 });
  const [expandedId, setExpandedId] = useState(1);

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const getTypeColor = (type) => {
    const colors = {
      Leadership: 'from-amber-400 to-orange-500',
      Management: 'from-blue-400 to-indigo-600',
      Academic: 'from-emerald-400 to-green-600',
      Research: 'from-purple-400 to-violet-600',
      Development: 'from-rose-400 to-pink-600'
    };
    return colors[type] || 'from-gray-500 to-gray-600';
  };

  const getTypeShadow = (type) => {
    const shadows = {
      Leadership: 'shadow-amber-500/30',
      Management: 'shadow-blue-500/30',
      Academic: 'shadow-emerald-500/30',
      Research: 'shadow-purple-500/30',
      Development: 'shadow-rose-500/30'
    };
    return shadows[type] || 'shadow-gray-500/30';
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'Leadership': return <Award className="w-5 h-5" />;
      case 'Management': return <Briefcase className="w-5 h-5" />;
      case 'Academic': return <GraduationCap className="w-5 h-5" />;
      case 'Research': return <Users className="w-5 h-5" />;
      default: return <Building2 className="w-5 h-5" />;
    }
  };

  const statColors = [
    { gradient: 'from-emerald-400 to-green-600', shadow: 'shadow-emerald-500/30' },
    { gradient: 'from-amber-400 to-orange-500', shadow: 'shadow-amber-500/30' },
    { gradient: 'from-blue-400 to-indigo-600', shadow: 'shadow-blue-500/30' },
    { gradient: 'from-purple-400 to-violet-600', shadow: 'shadow-purple-500/30' },
  ];

  return (
    <>
      {/* Page Header */}
      <div className="relative bg-gradient-to-br from-[#1F3C88] via-[#2A4FA8] to-[#1F3C88] py-32 overflow-hidden" ref={headerRef}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        </div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-500/20 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className={`transition-all duration-700 ${headerInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <span className="inline-block px-5 py-2 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-sm font-bold rounded-full mb-6 shadow-lg shadow-amber-500/30">
              CAREER PATH
            </span>
            <h1 className="text-5xl lg:text-7xl font-black text-white mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Professional <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-orange-400">Experience</span>
            </h1>
            <p className="text-white/80 max-w-2xl text-xl leading-relaxed font-medium">
              Over a decade of leadership in academia, government institutions, and development organizations.
            </p>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { number: "10+", label: "Years Experience" },
              { number: "18K+", label: "Staff Managed" },
              { number: "4", label: "Universities" },
              { number: "$3.75M+", label: "Budget Managed" },
            ].map((stat, index) => (
              <div
                key={index}
                className={`group rounded-3xl p-8 bg-gradient-to-br ${statColors[index].gradient} overflow-hidden transition-all duration-500 hover:scale-105 hover:-translate-y-2 shadow-xl ${statColors[index].shadow}`}
              >
                <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                <p className="text-4xl lg:text-5xl font-black text-white mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  {stat.number}
                </p>
                <p className="text-white/90 font-semibold">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white" ref={timelineRef}>
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-700 ${timelineInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <span className="inline-block px-5 py-2 bg-gradient-to-r from-blue-400 to-indigo-600 text-white text-sm font-bold rounded-full mb-4 shadow-lg shadow-blue-500/30">
              TIMELINE
            </span>
            <h2 className="text-4xl lg:text-5xl font-black text-[#1C1C1C] mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Career <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-600">Journey</span>
            </h2>
          </div>

          {/* Experience Items */}
          <div className="space-y-6">
            {experience.map((exp, index) => (
              <div
                key={exp.id}
                className={`transition-all duration-700 ${timelineInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div
                  className={`group bg-white rounded-3xl shadow-lg cursor-pointer transition-all duration-500 overflow-hidden hover:shadow-2xl ${
                    expandedId === exp.id ? 'ring-2 ring-[#1F3C88]' : 'hover:-translate-y-1'
                  }`}
                  onClick={() => toggleExpand(exp.id)}
                >
                  <div className="p-8">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-3 mb-4">
                          <span className={`bg-gradient-to-r ${getTypeColor(exp.type)} text-white px-4 py-1.5 rounded-full text-sm font-bold inline-flex items-center gap-2 shadow-lg ${getTypeShadow(exp.type)}`}>
                            {getTypeIcon(exp.type)}
                            {exp.type}
                          </span>
                          <span className="inline-flex items-center gap-2 text-[#718096] text-sm bg-gray-100 px-3 py-1.5 rounded-full font-medium">
                            <Calendar className="w-4 h-4" />
                            {exp.period}
                          </span>
                        </div>
                        <h3 className="text-2xl font-black text-[#1C1C1C] mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                          {exp.role}
                        </h3>
                        <div className="flex items-center gap-2 text-[#1F3C88] font-bold">
                          <Building2 className="w-5 h-5" />
                          <span>{exp.organization}</span>
                        </div>
                        <p className="text-[#C9A24D] text-sm mt-3 font-bold flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          {exp.highlights}
                        </p>
                      </div>
                      <button className={`p-3 rounded-xl transition-all duration-300 ${
                        expandedId === exp.id 
                          ? 'bg-gradient-to-r from-[#1F3C88] to-[#2A4FA8] text-white' 
                          : 'bg-gray-100 text-[#1F3C88] hover:bg-[#1F3C88] hover:text-white'
                      }`}>
                        {expandedId === exp.id ? (
                          <ChevronUp className="w-6 h-6" />
                        ) : (
                          <ChevronDown className="w-6 h-6" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className={`overflow-hidden transition-all duration-500 ${expandedId === exp.id ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="px-8 pb-8 border-t border-gray-100">
                      <h4 className="text-[#1F3C88] font-black uppercase tracking-wider mt-6 mb-4 flex items-center gap-2">
                        <span className="w-8 h-1 bg-gradient-to-r from-amber-400 to-orange-500 rounded"></span>
                        Key Responsibilities
                      </h4>
                      <ul className="space-y-3">
                        {exp.responsibilities.map((resp, idx) => (
                          <li key={idx} className="flex items-start gap-3 text-[#4A5568]">
                            <span className="w-6 h-6 bg-gradient-to-br from-emerald-400 to-green-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 shadow-lg shadow-emerald-500/30">
                              <CheckCircle className="w-3.5 h-3.5 text-white" />
                            </span>
                            <span className="leading-relaxed">{resp}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default ExperiencePage;